import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Wallet, Building2, ListPlus, ScanFace, Ticket, HandCoins, PiggyBank, Users, TrendingUp, Receipt, CheckCircle2, Activity, ArrowRight } from "lucide-react";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Button } from "../components/ui/button";
import { Money, StatusBadge, EmptyState } from "../components/common";
import { PeriodPicker } from "../components/inputs";
import { api, errMsg } from "../lib/api";
import { currentPeriod, num, rp } from "../lib/format";
import { toast } from "sonner";

const TONES = {
  rose: "bg-rose-50 text-[#E11D5A]",
  amber: "bg-amber-50 text-amber-600",
  sky: "bg-sky-50 text-sky-600",
  emerald: "bg-emerald-50 text-emerald-600",
  violet: "bg-violet-50 text-violet-600",
  red: "bg-red-50 text-red-600",
};

const Kpi = ({ label, children, testid, icon: Icon, tone = "rose", valueCls = "text-gray-900", sub }) => (
  <div data-testid={testid} className="fade-up rounded-2xl border bg-white p-4 sm:p-5">
    <div className="flex items-start justify-between gap-3">
      <p className="text-[13px] font-medium text-gray-500">{label}</p>
      <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${TONES[tone]}`}><Icon className="h-[18px] w-[18px]" /></span>
    </div>
    <div data-testid={testid && `${testid}-value`} className={`mt-1 whitespace-nowrap text-xl font-semibold tabular-nums sm:text-2xl ${valueCls}`}>{children}</div>
    {sub && <p className="mt-1 text-xs text-gray-500">{sub}</p>}
  </div>
);

const Card = ({ title, icon: Icon, action, children, className = "", testid }) => (
  <section data-testid={testid} className={`fade-up rounded-2xl border bg-white ${className}`}>
    <div className="flex items-center justify-between gap-3 border-b border-gray-100 px-5 py-4">
      <h2 className="flex items-center gap-2 text-[15px] font-semibold text-gray-900">{Icon && <Icon className="h-4 w-4 text-[#E11D5A]" />}{title}</h2>
      {action}
    </div>
    {children}
  </section>
);

const greeting = () => {
  const h = new Date().getHours();
  return h < 11 ? "Selamat pagi" : h < 15 ? "Selamat siang" : h < 19 ? "Selamat sore" : "Selamat malam";
};

const shortLabel = (l) => l.replace(/^(\d+)–(\d+) (\w{3})\w* \d{4}$/, "$1–$2 $3");

const TrendChart = ({ data }) => (
  <div className="h-64 px-2 pb-3 pt-4" data-testid="dashboard-trend-chart">
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data.map((d) => ({ ...d, nama: shortLabel(d.label) }))} margin={{ top: 4, right: 12, left: -12, bottom: 0 }}>
        <defs>
          <linearGradient id="barGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#F0603F" />
            <stop offset="100%" stopColor="#E11D5A" />
          </linearGradient>
        </defs>
        <CartesianGrid vertical={false} stroke="#F0F1F4" />
        <XAxis dataKey="nama" tickLine={false} axisLine={false} tick={{ fontSize: 12, fill: "#6B7280" }} />
        <YAxis allowDecimals={false} tickLine={false} axisLine={false} tick={{ fontSize: 12, fill: "#9CA3AF" }} width={40} />
        <Tooltip cursor={{ fill: "#FFF1F4" }} contentStyle={{ borderRadius: 12, border: "1px solid #ECEEF2", boxShadow: "0 6px 20px -6px rgba(16,24,40,.15)", fontSize: 13 }}
          formatter={(v, _n, p) => [`${num(v)} voucher · ${rp(p.payload.gaji_kotor)}`, "Total"]} labelFormatter={(_l, p) => p?.[0]?.payload.label} />
        <Bar dataKey="voucher" fill="url(#barGrad)" radius={[8, 8, 0, 0]} maxBarSize={44} />
      </BarChart>
    </ResponsiveContainer>
  </div>
);

const QUICK = [
  ["/voucher", "Tambah voucher", Ticket, "rose", "quick-voucher"],
  ["/voucher/massal", "Input massal", ListPlus, "violet", "quick-massal"],
  ["/kasbon", "Catat kasbon", HandCoins, "amber", "quick-kasbon"],
  ["/gajian", "Proses gajian", Wallet, "emerald", "quick-gajian"],
  ["/kehadiran", "Cek kehadiran", ScanFace, "sky", "quick-kehadiran"],
];

const ACT = {
  voucher: [Ticket, "rose"], kasbon: [HandCoins, "amber"], bayar: [CheckCircle2, "emerald"], hadir: [ScanFace, "sky"],
};
const fmtWaktu = (w) => new Date(w).toLocaleString("id-ID", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
const actText = (a) => a.jenis === "kasbon" ? `Kasbon ${rp(a.jumlah)}` : a.jenis === "bayar" ? `Gaji dibayar ${rp(a.jumlah)}` : a.teks;

const ActivityList = ({ items }) => items.length === 0 ? (
  <p className="px-5 py-10 text-center text-sm text-gray-500" data-testid="dashboard-activity-empty">Belum ada aktivitas.</p>
) : (
  <ul className="divide-y divide-gray-100" data-testid="dashboard-activity">
    {items.map((a, i) => {
      const [Icon, tone] = ACT[a.jenis] || [Activity, "rose"];
      return (
        <li key={i} className="flex items-start gap-3 px-5 py-3" data-testid={`activity-${i}`}>
          <span className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${TONES[tone]}`}><Icon className="h-4 w-4" /></span>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-gray-900">{a.anak_nama}</p>
            <p className="text-[13px] text-gray-500">{actText(a)}</p>
          </div>
          <span className="shrink-0 text-xs text-gray-400">{fmtWaktu(a.waktu)}</span>
        </li>
      );
    })}
  </ul>
);

export default function Dashboard() {
  const [period, setPeriod] = useState(currentPeriod());
  const [d, setD] = useState(null);
  const [tren, setTren] = useState([]);
  const [akt, setAkt] = useState([]);

  useEffect(() => {
    api.get("/dashboard", { params: { period } }).then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e)));
    api.get("/dashboard/tren", { params: { period } }).then((r) => setTren(r.data)).catch(() => {});
  }, [period]);
  useEffect(() => { api.get("/dashboard/aktivitas").then((r) => setAkt(r.data)).catch(() => {}); }, []);

  const t = d?.totals || {};
  return (
    <div>
      <div className="fade-up mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-[13px] font-medium text-[#E11D5A]" data-testid="dashboard-greeting">{greeting()}, Admin</p>
          <h1 data-testid="dashboard-title" className="font-heading text-[26px] font-semibold leading-tight text-gray-900 sm:text-[28px]">Beranda</h1>
          <p className="mt-1 text-[13px] text-gray-500">{d ? `Tarif ${rp(d.tarif)} / voucher · ${d.anak_aktif} anak aktif · ${d.jumlah_perusahaan} perusahaan` : "Memuat…"}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button asChild variant="outline" className="h-10 rounded-full"><Link to="/gajian" data-testid="dashboard-open-gajian"><Wallet className="mr-1.5 h-4 w-4" />Gajian</Link></Button>
          <Button asChild variant="outline" className="h-10 rounded-full"><Link to="/voucher/massal" data-testid="dashboard-open-massal"><ListPlus className="mr-1.5 h-4 w-4" />Input Massal</Link></Button>
          <Button asChild className="h-10 rounded-full px-5"><Link to="/voucher" data-testid="dashboard-add-voucher"><Plus className="mr-1.5 h-4 w-4" />Voucher</Link></Button>
        </div>
      </div>

      <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="sm:w-80"><PeriodPicker value={period} onChange={setPeriod} testid="dashboard-period" /></div>
        {d && <StatusBadge status={d.status} testid="dashboard-status" />}
      </div>

      <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        <div data-testid="kpi-gaji-bersih" className="fade-up relative col-span-2 overflow-hidden rounded-2xl bg-primary p-5 text-white shadow-[0_10px_30px_-12px_rgba(225,29,90,0.6)]">
          <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-white/10" />
          <div className="absolute -bottom-16 right-16 h-36 w-36 rounded-full bg-white/10" />
          <p className="relative flex items-center gap-2 text-[13px] font-medium text-white/85"><Receipt className="h-4 w-4" />Gaji bersih periode ini</p>
          <p data-testid="kpi-gaji-bersih-value" className="relative mt-2 whitespace-nowrap text-3xl font-semibold tabular-nums sm:text-4xl">{rp(t.gaji_bersih)}</p>
          <p className="relative mt-2 text-[13px] text-white/80">{d?.label} · {num(d?.jumlah_anak)} anak</p>
        </div>
        <Kpi label="Gaji kotor" testid="kpi-gaji-kotor" icon={Wallet} tone="violet"><Money value={t.gaji_kotor} /></Kpi>
        <Kpi label="Potongan kasbon" testid="kpi-potongan" icon={HandCoins} tone="red" valueCls="text-red-600"><Money value={t.potongan} /></Kpi>
        <Kpi label="Total voucher" testid="kpi-total-voucher" icon={Ticket} tone="rose"><span>{num(t.voucher)}</span></Kpi>
        <Kpi label="Sisa kasbon" testid="kpi-sisa-kasbon" icon={PiggyBank} tone="amber" valueCls="text-amber-600"><Money value={t.sisa_kasbon} /></Kpi>
        <Kpi label="Anak di periode ini" testid="kpi-jumlah-anak" icon={Users} tone="sky"><span>{num(d?.jumlah_anak)}</span></Kpi>
        <Link to="/kehadiran" data-testid="kpi-hadir-hari-ini" className="fade-up rounded-2xl border bg-white p-4 transition-shadow hover:shadow-md sm:p-5 card-soft">
          <div className="flex items-start justify-between gap-3">
            <p className="text-[13px] font-medium text-gray-500">Hadir hari ini</p>
            <span className={`flex h-9 w-9 items-center justify-center rounded-xl ${TONES.emerald}`}><ScanFace className="h-[18px] w-[18px]" /></span>
          </div>
          <p data-testid="kpi-hadir-hari-ini-value" className="mt-1 text-xl font-semibold tabular-nums text-emerald-600 sm:text-2xl">{d?.hadir_hari_ini ?? 0} / {d?.anak_aktif ?? 0}</p>
        </Link>
      </div>

      <div className="mt-5 grid gap-4 lg:grid-cols-3">
        <Card title="Tren voucher per periode" icon={TrendingUp} className="lg:col-span-2" testid="dashboard-trend"
          action={<span className="text-xs text-gray-500">{tren.length} periode terakhir</span>}>
          <TrendChart data={tren} />
        </Card>
        <Card title="Aksi cepat" icon={Plus} testid="dashboard-quick">
          <div className="grid grid-cols-2 gap-2 p-4 lg:grid-cols-1">
            {QUICK.map(([to, label, Icon, tone, tid]) => (
              <Link key={to} to={to} data-testid={tid} className="group flex items-center gap-3 rounded-xl border border-gray-100 px-3 py-2.5 transition-colors hover:border-rose-200 hover:bg-rose-50/50">
                <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${TONES[tone]}`}><Icon className="h-4 w-4" /></span>
                <span className="flex-1 text-sm font-medium text-gray-800">{label}</span>
                <ArrowRight className="hidden h-4 w-4 text-gray-300 transition-transform group-hover:translate-x-0.5 group-hover:text-[#E11D5A] lg:block" />
              </Link>
            ))}
          </div>
        </Card>
      </div>

      <div className="mt-5 grid items-start gap-4 lg:grid-cols-3">
        <Card title="Rincian per perusahaan" icon={Building2} className="overflow-hidden lg:col-span-2" testid="dashboard-breakdown-card">
          {d && d.per_perusahaan.length === 0 ? (
            <div className="p-4"><EmptyState icon={Building2} title="Belum ada data di periode ini" desc="Tambahkan voucher atau kasbon untuk melihat rincian." /></div>
          ) : (
            <div data-testid="dashboard-breakdown">
              <div className="hidden grid-cols-[2fr_repeat(5,1fr)] gap-4 bg-gray-50/70 px-5 py-2.5 text-xs font-medium text-gray-500 md:grid">
                <span>Perusahaan</span><span className="text-right">Voucher</span><span className="text-right">Kotor</span><span className="text-right">Potongan</span><span className="text-right">Bersih</span><span className="text-right">Sisa kasbon</span>
              </div>
              {(d?.per_perusahaan || []).map((p) => (
                <div key={p.perusahaan_id || "none"} data-testid={`breakdown-row-${p.perusahaan_id || "none"}`}
                  className="grid grid-cols-2 gap-x-4 gap-y-2 border-t border-gray-100 px-5 py-3.5 md:grid-cols-[2fr_repeat(5,1fr)] md:items-center">
                  <div className="col-span-2 md:col-span-1">
                    <p className="font-medium text-gray-900">{p.perusahaan_nama}</p>
                    <p className="text-xs text-gray-500">{p.jumlah_anak} anak</p>
                  </div>
                  <Cell label="Voucher" value={num(p.voucher)} />
                  <Cell label="Kotor" value={rp(p.gaji_kotor)} />
                  <Cell label="Potongan" value={rp(p.potongan)} tone="text-red-600" />
                  <Cell label="Bersih" value={rp(p.gaji_bersih)} tone="text-gray-900 font-semibold" />
                  <Cell label="Sisa kasbon" value={rp(p.sisa_kasbon)} tone="text-amber-600" />
                </div>
              ))}
            </div>
          )}
        </Card>
        <Card title="Aktivitas terbaru" icon={Activity} testid="dashboard-activity-card"><ActivityList items={akt} /></Card>
      </div>
    </div>
  );
}

const Cell = ({ label, value, tone = "text-gray-700" }) => (
  <div className="md:text-right">
    <p className="text-xs text-gray-500 md:hidden">{label}</p>
    <p className={`whitespace-nowrap text-[15px] tabular-nums ${tone}`}>{value}</p>
  </div>
);
